// External Libraries
import {
  Suspense,
  useCallback,
  useEffect,
  useLayoutEffect,
} from "react";
import { Routes, Route, Navigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { ThunkDispatch } from "redux-thunk";
import { AnyAction } from "redux";
import Cookies from "universal-cookie";
import LoadingBar from "react-top-loading-bar";
import { TailSpin } from "react-loader-spinner";

// Styles
import "./App.scss";
import "yet-another-react-lightbox/styles.css";

// Constants and Services
import { RoutesConstants } from "constants/RoutesConstants";
import apiLibrary from "services/api";
import { convertKeysToCamelCase } from "utils/caseConvertor";
import { profileSettingsAction } from "store/userProfilesettings";
import initHotJar from "utils/hotJarSetup";
import { currentEnvironment } from "config";
import { removeExpiredFiltersFromLocalStorage } from "utils/cacheOrRetriveFiltersDate";
import { getCurrentBranding } from "utils/customBranding";

// Hooks
import useRoles from "hooks/roles";
import usePermissions from "hooks/usePermissions";
import useCustomBranding from "hooks/useCustomBranding";
import { useThemeColor } from "hooks/useThemeColor";

// Layout and Components
import Layout from "view/layouts/Layout";
import Head from "view/components/Head";

// Routes Protection
import {
  AuthRoutes,
  ProtectedRoutes,
  SpeacialProtectedRoutes,
} from "utils/RoutesProtection";

// Redux and Store
import { RootState } from "store";
import { DarkMode } from "store/darkMode/initialState";

import * as Pages from "view/pages";
import { fetchUnreadNotificationsStatusAction } from "store/unreadNotifications";
function App() {
  const cookies = new Cookies();
  const getAuthToken = () => cookies.get("token");
  const { isThemeLoaded, theme, primaryColor } = useCustomBranding();

  const {
    isExternalReviewer,
    isExternalDataEndReviewer,
    isInternalEndDataViewer,
    isUserInternalReviewer,
  } = useRoles();
  const progress = useSelector((state: RootState) => state.loadingBar.progress);
  const auth = useSelector((state: RootState) => state.auth);
  const {
    roles,
    tags,
    universalSettings,
    surveyAndPolls,
    reports,
    reminders,
    notifications,
    dashboard,
    conversations,
    geographies,
    communities,
    species,
    objects,
    behaviors,
    conditions,
    imports,
    categories,
    types,
    qa,
    observations,
    programs,
    file,
    dynamicForms,
    protocols,
    organizations,
    users,
    profile,
    trackings,
    markerGroups,
    markerStyles,
    brands,
  } = usePermissions();

  const dispatch: ThunkDispatch<any, any, AnyAction> = useDispatch();

  const fetchUserProfileSettings = useCallback(
    async (personId: number | null) => {
      try {
        const res = await apiLibrary.Settings.userProfileSettings(personId);
        const settings = convertKeysToCamelCase(res.data);
        dispatch(profileSettingsAction(settings));
        // Process other data as needed
      } catch (error: any) {
        console.error(error);
        // Handle error
      }
    },
    [dispatch]
  );

  useLayoutEffect(() => {
    if (auth?.id && getAuthToken() && !isExternalReviewer()) {
      fetchUserProfileSettings(auth.id);
    }
  }, [auth?.id, fetchUserProfileSettings]);

  const { isDarkMode } = useSelector<RootState, DarkMode>(
    (state) => state?.darkMode
  );

  useLayoutEffect(() => {
    localStorage.removeItem("brands-themes");
  }, []);

  useLayoutEffect(() => {
    if (isDarkMode) {
      document.body.classList.add("dark");
    } else {
      document.body.classList.remove("dark");
    }
  }, [isDarkMode]);

  useEffect(() => {
    if (
      auth.roles.includes("super_admin") &&
      currentEnvironment !== "localhost"
    ) {
      initHotJar(auth);
    }
  }, [auth.id]);

  useEffect(() => {
    const intervalId = setInterval(() => {
      removeExpiredFiltersFromLocalStorage();
    }, 15000);
    return () => clearInterval(intervalId);
  }, []);

  useEffect(() => {
    if (auth.accessToken && notifications.canViewNotifications) {
      dispatch(fetchUnreadNotificationsStatusAction());

      const intervalId = setInterval(() => {
        dispatch(fetchUnreadNotificationsStatusAction(true));
      }, 15000);

      return () => clearInterval(intervalId);
    }
  }, [auth.accessToken, notifications.canViewNotifications, dispatch]);

  if (!isThemeLoaded) {
    return (
      <div className="loader min-h-[45vh] justify-center items-center flex w-full">
        <TailSpin
          height="50"
          width="50"
          color={useThemeColor()}
          ariaLabel="tail-spin-loading"
          radius="2"
          wrapperStyle={{}}
          wrapperClass="tailspin-loader"
          visible={true}
        />
      </div>
    );
  }

  return (
    <div className={`${isDarkMode && "dark"} ${getCurrentBranding()}`}>
      <LoadingBar
        color={primaryColor}
        progress={progress}
        height={3}
        onLoaderFinished={() => {}}
      />
      {/* Default title */}
      <Head />
      <Routes>
        <Route
          path={RoutesConstants.filesSearch}
          element={
            <AuthRoutes
              component={Pages.QueryInterface}
              permission={
                theme?.brandName === "Seacoast Indigenous Guardians Network"
              }
            />
          }
        />
        <Route path="/auth/*">
          {/* Other authentication-related routes */}
          <Route
            path={RoutesConstants.login}
            element={<AuthRoutes component={Pages.SignIn} />}
          />
          <Route
            path={RoutesConstants.forgetPassword}
            element={<AuthRoutes component={Pages.ForgetPassword} />}
          />
          <Route
            path={RoutesConstants.createPassword}
            element={
              <SpeacialProtectedRoutes component={Pages.CreatePassword} />
            }
          />
          <Route path="*" element={<Navigate to={RoutesConstants.login} />} />
        </Route>

        {isExternalDataEndReviewer() ? (
          <Route path="/" element={<Layout />}>
            <Route path="/" element={<Navigate to="/reports/list" replace />} />
            <Route path="/reports/*">
              <Route
                path={RoutesConstants.list}
                element={
                  <ProtectedRoutes
                    component={Pages.Reports}
                    permissions={reports.canViewReports}
                    fallbackPath="/"
                  />
                }
              />
              <Route
                path={RoutesConstants.addReport}
                element={
                  <ProtectedRoutes
                    component={Pages.AddEditReport}
                    permissions={reports.canCreateReports}
                    fallbackPath={-1}
                  />
                }
              />

              <Route path={RoutesConstants.reportsId}>
                <Route
                  path={RoutesConstants.editReport}
                  element={
                    <ProtectedRoutes
                      component={Pages.AddEditReport}
                      permissions={reports.canEditReports}
                      fallbackPath={-1}
                    />
                  }
                />
                <Route
                  path={RoutesConstants.analytics}
                  element={
                    <ProtectedRoutes
                      component={Pages.ReportsAnalytics}
                      permissions={reports.canViewAnalyticsReports}
                      fallbackPath={-1}
                    />
                  }
                />
                <Route
                  path={RoutesConstants.profile}
                  element={
                    <ProtectedRoutes
                      component={Pages.ReportsProfile}
                      permissions={reports.canViewReports}
                      fallbackPath={-1}
                    />
                  }
                />
                <Route
                  path={RoutesConstants.runReport}
                  element={
                    <ProtectedRoutes
                      component={Pages.RunReport}
                      permissions={reports.canViewProcessedVersionReports}
                      fallbackPath={-1}
                    />
                  }
                />
                <Route
                  path={RoutesConstants.reportVersion}
                  element={
                    <ProtectedRoutes
                      component={Pages.ResultObservations}
                      permissions={reports.canViewResultReports}
                      fallbackPath={-1}
                    />
                  }
                />
              </Route>
              <Route
                path="*"
                element={
                  <Suspense fallback={<SuspenseFallback />}>
                    <Pages.PageNotFound />
                  </Suspense>
                }
              />
            </Route>
          </Route>
        ) : isExternalReviewer() ? (
          <Route path="/*" element={<Layout />}>
            <Route path="quality-assurance/*">
              <Route
                path={RoutesConstants.list}
                element={
                  <ProtectedRoutes
                    component={Pages.QualityAssurance}
                    permissions={qa.canViewReviewerObservations}
                    fallbackPath={-1}
                  />
                }
              />
              <Route path={RoutesConstants.observationId}>
                <Route
                  path={RoutesConstants.profile}
                  element={
                    <ProtectedRoutes
                      component={Pages.ObservationProfile}
                      permissions={observations.canViewObservations}
                      fallbackPath={-1}
                    />
                  }
                />
              </Route>
              <Route path={RoutesConstants.qualityAssuranceId}>
                <Route
                  path={RoutesConstants.profile}
                  element={
                    <ProtectedRoutes
                      component={Pages.QualityAssuranceProfile}
                    />
                  }
                />
              </Route>

              <Route
                path="*"
                element={
                  <Suspense fallback={<SuspenseFallback />}>
                    <Pages.PageNotFound />
                  </Suspense>
                }
              />
            </Route>
            <Route path="protocols/*">
              <Route path={RoutesConstants.protocolId}>
                <Route
                  path={RoutesConstants.profile}
                  element={
                    <ProtectedRoutes
                      component={Pages.ProtocolProfile}
                      permissions={true}
                      fallbackPath={-1}
                    />
                  }
                />
              </Route>
            </Route>

            <Route
              path="*"
              element={
                <Suspense fallback={<SuspenseFallback />}>
                  <Pages.PageNotFound />
                </Suspense>
              }
            />
          </Route>
        ) : (
          <Route path="/*" element={<Layout />}>
            <Route
              path=""
              element={
                <ProtectedRoutes
                  component={Pages.Dashboard}
                  permissions={dashboard.canViewDashboardConfiguration}
                  fallbackPath={
                    !dashboard.canViewDashboardConfiguration &&
                    isInternalEndDataViewer()
                      ? "/reports/list"
                      : isUserInternalReviewer()
                        ? "quality-assurance/list"
                        : -1
                  }
                />
              }
            />
            <Route
              path="profile/:personId/*"
              element={
                <ProtectedRoutes
                  component={Pages.ProfileLayout}
                  permissions={users.canViewUsersProfile}
                  fallbackPath={-1}
                />
              }
            >
              <Route
                path={RoutesConstants.person}
                element={
                  <ProtectedRoutes
                    component={Pages.Person}
                    permissions={users.canViewUsersProfile}
                    fallbackPath={-1}
                  />
                }
              />
              <Route
                path={RoutesConstants.account}
                element={
                  <ProtectedRoutes
                    component={Pages.Account}
                    permissions={users.canViewUsersProfile}
                    fallbackPath={-1}
                  />
                }
              />
              <Route
                path={RoutesConstants.changePassword}
                element={<ProtectedRoutes component={Pages.ChangePassword} />}
              />
              <Route
                path={RoutesConstants.notifications}
                element={
                  <ProtectedRoutes
                    component={Pages.Notification}
                    permissions={users.canViewNotification}
                    fallbackPath={-1}
                  />
                }
              />
              <Route
                path={RoutesConstants.roles}
                element={
                  <ProtectedRoutes
                    component={Pages.Roles}
                    permissions={profile.canViewRolesProfile}
                    fallbackPath={-1}
                  />
                }
              />
              <Route
                path={RoutesConstants.settings}
                element={
                  <ProtectedRoutes
                    component={Pages.Settings}
                    permissions={users.canViewSettings}
                    fallbackPath={-1}
                  />
                }
              />
              <Route
                path={RoutesConstants.deleteAccount}
                element={<ProtectedRoutes component={Pages.DeleteAccount} />}
              />

              <Route
                path="*"
                element={
                  <Suspense fallback={<SuspenseFallback />}>
                    <Pages.PageNotFound />
                  </Suspense>
                }
              />
            </Route>

            <Route path="community/*">
              <Route
                path={RoutesConstants.list}
                element={
                  <ProtectedRoutes
                    component={Pages.Communities}
                    permissions={
                      communities.canViewViewCommunities &&
                      communities.canViewSideBarCommunities
                    }
                    fallbackPath={-1}
                  />
                }
              />
              <Route path={RoutesConstants.communityId}>
                <Route
                  path={RoutesConstants.profile}
                  element={
                    <ProtectedRoutes
                      component={Pages.CommunityProfile}
                      permissions={communities.canViewViewCommunities}
                      fallbackPath={-1}
                    />
                  }
                />
                <Route
                  path={RoutesConstants.users}
                  element={
                    <ProtectedRoutes
                      component={Pages.UserPage}
                      permissions={users.canViewUsers}
                      fallbackPath={-1}
                    />
                  }
                />
                <Route
                  path={RoutesConstants.organizations}
                  element={
                    <ProtectedRoutes
                      component={Pages.Organizations}
                      permissions={organizations.canViewOrganization}
                      fallbackPath={-1}
                    />
                  }
                />

                <Route
                  path={"forms"}
                  element={
                    <ProtectedRoutes
                      component={Pages.Forms}
                      permissions={dynamicForms.canViewDynamicForm}
                      fallbackPath={-1}
                    />
                  }
                />

                <Route
                  path={RoutesConstants.observations}
                  element={
                    <ProtectedRoutes
                      component={Pages.Observations}
                      permissions={observations.canViewObservations}
                      fallbackPath={-1}
                    />
                  }
                />

                <Route
                  path="programs"
                  element={
                    <ProtectedRoutes
                      component={Pages.Programs}
                      permissions={programs.canViewProgram}
                      fallbackPath={-1}
                    />
                  }
                />
                <Route
                  path={RoutesConstants.geography}
                  element={
                    <ProtectedRoutes
                      component={Pages.GeographyMap}
                      permissions={
                        communities.canViewGeographyMapListCommunities
                      }
                      fallbackPath={-1}
                    />
                  }
                />

                <Route
                  path="species"
                  element={
                    <ProtectedRoutes
                      component={Pages.Species}
                      permissions={species.canViewSpecies}
                      fallbackPath={-1}
                    />
                  }
                />

                <Route path={`species/${RoutesConstants.specieId}`}>
                  <Route
                    path={RoutesConstants.profile}
                    element={
                      <ProtectedRoutes
                        component={Pages.SpecieProfile}
                        permissions={species.canViewSpecies}
                        fallbackPath={-1}
                      />
                    }
                  />
                </Route>

                <Route
                  path="objects"
                  element={
                    <ProtectedRoutes
                      component={Pages.Objects}
                      permissions={objects.canViewObjects}
                      fallbackPath={-1}
                    />
                  }
                />

                <Route path={`object/${RoutesConstants.objectId}`}>
                  <Route
                    path={RoutesConstants.profile}
                    element={
                      <ProtectedRoutes
                        component={Pages.ObjectProfile}
                        permissions={objects.canViewObjects}
                        fallbackPath={-1}
                      />
                    }
                  />
                </Route>

                <Route
                  path={"quality-assurance"}
                  element={
                    <ProtectedRoutes
                      component={Pages.QualityAssurance}
                      permissions={qa.canViewQa}
                      fallbackPath={-1}
                    />
                  }
                />
              </Route>
              <Route
                path="*"
                element={
                  <Suspense fallback={<SuspenseFallback />}>
                    <Pages.PageNotFound />
                  </Suspense>
                }
              />
            </Route>

            <Route path="users/*">
              <Route
                path="list"
                element={
                  <ProtectedRoutes
                    component={Pages.UserPage}
                    permissions={
                      users.canViewUsers && users.canViewUsersInSideBar
                    }
                    fallbackPath={-1}
                  />
                }
              />
              <Route
                path="*"
                element={
                  <Suspense fallback={<SuspenseFallback />}>
                    <Pages.PageNotFound />
                  </Suspense>
                }
              />
            </Route>

            <Route path="organizations/*">
              <Route
                path={RoutesConstants.list}
                element={
                  <ProtectedRoutes
                    component={Pages.Organizations}
                    permissions={
                      organizations.canViewOrganization &&
                      organizations.canViewOrganizationDirectory
                    }
                    fallbackPath={-1}
                  />
                }
              />
              <Route path={RoutesConstants.organizationsId}>
                <Route
                  path={RoutesConstants.users}
                  element={
                    <ProtectedRoutes
                      component={Pages.UserPage}
                      permissions={users.canViewUsers}
                      fallbackPath={-1}
                    />
                  }
                />
                <Route
                  path={RoutesConstants.communities}
                  element={
                    <ProtectedRoutes
                      component={Pages.Communities}
                      permissions={communities.canViewViewCommunities}
                      fallbackPath={-1}
                    />
                  }
                />
                <Route
                  path="programs"
                  element={
                    <ProtectedRoutes
                      component={Pages.Programs}
                      permissions={programs.canViewProgram}
                      fallbackPath={-1}
                    />
                  }
                />
                <Route
                  path="profile"
                  element={
                    <ProtectedRoutes
                      component={Pages.OrganizationProfile}
                      permissions={organizations.canViewOrganization}
                      fallbackPath={-1}
                    />
                  }
                />
              </Route>
              <Route
                path="*"
                element={
                  <Suspense fallback={<SuspenseFallback />}>
                    <Pages.PageNotFound />
                  </Suspense>
                }
              />
            </Route>

            <Route path="forms/*">
              <Route
                path="list"
                element={
                  <ProtectedRoutes
                    component={Pages.Forms}
                    permissions={
                      dynamicForms.canViewDynamicForm &&
                      dynamicForms.canViewDirectoryFromSideBarDynamicForm
                    }
                    fallbackPath={-1}
                  />
                }
              />
              <Route
                path=":formId/edit"
                element={
                  <ProtectedRoutes
                    component={Pages.EditFormDetails}
                    permissions={dynamicForms.canEditDynamicForm}
                    fallbackPath={-1}
                  />
                }
              />
              <Route
                path="*"
                element={
                  <Suspense fallback={<SuspenseFallback />}>
                    <Pages.PageNotFound />
                  </Suspense>
                }
              />
            </Route>

            <Route path="programs/*">
              <Route
                path="list"
                element={
                  <ProtectedRoutes
                    component={Pages.Programs}
                    permissions={
                      programs.canViewProgram &&
                      programs.canViewProgramDirectory
                    }
                    fallbackPath={-1}
                  />
                }
              />

              <Route path={RoutesConstants.programId}>
                <Route
                  path={RoutesConstants.profile}
                  element={
                    <ProtectedRoutes
                      component={Pages.ProgramProfile}
                      permissions={programs.canViewProgram}
                      fallbackPath={-1}
                    />
                  }
                />
                <Route
                  path={RoutesConstants.users}
                  element={
                    <ProtectedRoutes
                      component={Pages.UserPage}
                      permissions={users.canViewUsers}
                      fallbackPath={-1}
                    />
                  }
                />
                <Route
                  path={RoutesConstants.memberRequest}
                  element={
                    <ProtectedRoutes
                      component={Pages.JoinRequests}
                      permissions={programs.canViewProgram}
                      fallbackPath={-1}
                    />
                  }
                />

                <Route
                  path={"polls-and-surveys"}
                  element={
                    <ProtectedRoutes
                      component={Pages.PollsSurveys}
                      permissions={surveyAndPolls.canViewSurveyAndPolls}
                      fallbackPath={-1}
                    />
                  }
                />

                <Route
                  path={RoutesConstants.observations}
                  element={
                    <ProtectedRoutes
                      component={Pages.Observations}
                      permissions={observations.canViewObservations}
                      fallbackPath={-1}
                    />
                  }
                />

                <Route
                  path={RoutesConstants.organizations}
                  element={
                    <ProtectedRoutes
                      component={Pages.Organizations}
                      permissions={organizations.canViewOrganization}
                      fallbackPath={-1}
                    />
                  }
                />
                <Route
                  path={RoutesConstants.communities}
                  element={
                    <ProtectedRoutes
                      component={Pages.Communities}
                      permissions={programs.canViewCommunities}
                      fallbackPath={-1}
                    />
                  }
                />
                <Route
                  path="forms"
                  element={
                    <ProtectedRoutes
                      component={Pages.Forms}
                      permissions={programs.canViewForms}
                      fallbackPath={-1}
                    />
                  }
                />

                <Route
                  path={"protocols"}
                  element={
                    <ProtectedRoutes
                      component={Pages.Protocols}
                      permissions={protocols.canViewProtocol}
                      fallbackPath={-1}
                    />
                  }
                />

                <Route
                  path={RoutesConstants.files}
                  element={
                    <ProtectedRoutes
                      component={Pages.Files}
                      permissions={file.canViewFile}
                      fallbackPath={-1}
                    />
                  }
                />

                <Route
                  path={RoutesConstants.dataAccessPolicy}
                  element={
                    <ProtectedRoutes
                      component={Pages.DataAccessPolicy}
                      permissions={programs.canViewDataAccessPolicy}
                      fallbackPath={-1}
                    />
                  }
                />
                <Route
                  path={RoutesConstants.management}
                  element={
                    <ProtectedRoutes
                      component={Pages.Management}
                      permissions={programs.canViewManagers}
                      fallbackPath={-1}
                    />
                  }
                />
                <Route
                  path="reports"
                  element={
                    <ProtectedRoutes
                      component={Pages.Reports}
                      permissions={reports.canViewReports}
                      fallbackPath={-1}
                    />
                  }
                />
              </Route>

              <Route
                path="*"
                element={
                  <Suspense fallback={<SuspenseFallback />}>
                    <Pages.PageNotFound />
                  </Suspense>
                }
              />
            </Route>

            <Route path="protocols/*">
              <Route
                path={RoutesConstants.list}
                element={
                  <ProtectedRoutes
                    component={Pages.Protocols}
                    permissions={
                      protocols.canViewProtocol &&
                      protocols.canViewSideBarProtocol
                    }
                    fallbackPath={-1}
                  />
                }
              />

              <Route path={RoutesConstants.protocolId}>
                <Route
                  path={RoutesConstants.profile}
                  element={
                    <ProtectedRoutes
                      component={Pages.ProtocolProfile}
                      permissions={protocols.canViewProtocol}
                      fallbackPath={-1}
                    />
                  }
                />
              </Route>
              <Route
                path="*"
                element={
                  <Suspense fallback={<SuspenseFallback />}>
                    <Pages.PageNotFound />
                  </Suspense>
                }
              />
            </Route>

            <Route path="custom-notifications/*">
              <Route
                path={RoutesConstants.list}
                element={
                  <ProtectedRoutes
                    component={Pages.Notifications}
                    permissions={reminders.canViewViewReminders}
                    fallbackPath="/"
                  />
                }
              />

              <Route
                path={RoutesConstants.addNotification}
                element={
                  <ProtectedRoutes
                    component={Pages.AddEditNotification}
                    permissions={reminders.canCreateReminders}
                    fallbackPath={-1}
                  />
                }
              />

              <Route path={RoutesConstants.notificationId}>
                <Route
                  path={RoutesConstants.recipients}
                  element={
                    <ProtectedRoutes
                      component={Pages.NotificationRecipients}
                      permissions={reminders.canViewMembersReminders}
                      fallbackPath={-1}
                    />
                  }
                />
                <Route
                  path={RoutesConstants.status}
                  element={
                    <ProtectedRoutes
                      component={Pages.NotificationStatus}
                      permissions={reminders.canViewStatusHistoryReminders}
                      fallbackPath={-1}
                    />
                  }
                />
                <Route
                  path={RoutesConstants.editNotification}
                  element={
                    <ProtectedRoutes
                      component={Pages.AddEditNotification}
                      permissions={reminders.canEditReminders}
                      fallbackPath={-1}
                    />
                  }
                />
              </Route>
              <Route
                path="*"
                element={
                  <Suspense fallback={<SuspenseFallback />}>
                    <Pages.PageNotFound />
                  </Suspense>
                }
              />
            </Route>

            <Route path="imports/*">
              <Route
                path={RoutesConstants.list}
                element={
                  <ProtectedRoutes
                    component={Pages.Imports}
                    permissions={imports.canViewImports}
                    fallbackPath={-1}
                  />
                }
              />

              <Route
                path={RoutesConstants.addImport}
                element={
                  <ProtectedRoutes
                    component={Pages.AddImport}
                    permissions={imports.canCreateImports}
                    fallbackPath={-1}
                  />
                }
              />

              <Route
                path={RoutesConstants.viewImportsData}
                element={
                  <ProtectedRoutes
                    component={Pages.ViewImportsData}
                    permissions={imports.canViewViewDataImports}
                    fallbackPath={-1}
                  />
                }
              />

              <Route
                path="*"
                element={
                  <Suspense fallback={<SuspenseFallback />}>
                    <Pages.PageNotFound />
                  </Suspense>
                }
              />
            </Route>

            <Route path="polls-and-surveys/*">
              <Route
                path={RoutesConstants.list}
                element={
                  <ProtectedRoutes
                    component={Pages.PollsSurveys}
                    permissions={surveyAndPolls.canViewSurveyAndPolls}
                    fallbackPath="/"
                  />
                }
              />

              <Route
                path={RoutesConstants.viewSurveyResults}
                element={
                  <ProtectedRoutes
                    component={Pages.ViewSurveyResults}
                    permissions={surveyAndPolls.canViewResultsSurveyAndPolls}
                    fallbackPath={-1}
                  />
                }
              />

              <Route
                path={RoutesConstants.editSurveyId}
                element={
                  <ProtectedRoutes
                    component={Pages.CreateOrEditSurvey}
                    permissions={surveyAndPolls.canEditSurveyAndPolls}
                    fallbackPath={-1}
                  />
                }
              />

              {/* <Route
                path=":surveyId/add"
                element={ <Suspense fallback={<SuspenseFallback />}><Pages.ProtectedRoutes component={Pages.SurveyComponent} />}
              /> */}

              <Route
                path="*"
                element={
                  <Suspense fallback={<SuspenseFallback />}>
                    <Pages.PageNotFound />
                  </Suspense>
                }
              />
            </Route>

            <Route path="observations/*">
              <Route
                path={RoutesConstants.list}
                element={
                  <ProtectedRoutes
                    component={Pages.Observations}
                    permissions={observations.canViewObservations}
                    fallbackPath={-1}
                  />
                }
              />

              <Route path={RoutesConstants.observationId}>
                <Route
                  path={RoutesConstants.profile}
                  element={
                    <ProtectedRoutes
                      component={Pages.ObservationProfile}
                      permissions={observations.canViewObservations}
                      fallbackPath={-1}
                    />
                  }
                />
              </Route>
              <Route
                path="*"
                element={
                  <Suspense fallback={<SuspenseFallback />}>
                    <Pages.PageNotFound />
                  </Suspense>
                }
              />
            </Route>

            <Route path="quality-assurance/*">
              <Route
                path={RoutesConstants.list}
                element={
                  <ProtectedRoutes
                    component={Pages.QualityAssurance}
                    permissions={qa.canViewQa}
                    fallbackPath={-1}
                  />
                }
              />
              <Route path={RoutesConstants.observationId}>
                <Route
                  path={RoutesConstants.profile}
                  element={
                    <ProtectedRoutes
                      component={Pages.ObservationProfile}
                      permissions={observations.canViewObservations}
                      fallbackPath={-1}
                    />
                  }
                />
              </Route>
              <Route path={RoutesConstants.qualityAssuranceId}>
                <Route
                  path={RoutesConstants.profile}
                  element={
                    <ProtectedRoutes
                      component={Pages.QualityAssuranceProfile}
                    />
                  }
                />
              </Route>
              <Route
                path="*"
                element={
                  <Suspense fallback={<SuspenseFallback />}>
                    <Pages.PageNotFound />
                  </Suspense>
                }
              />
            </Route>
            <Route path="species/*">
              <Route
                path={RoutesConstants.list}
                element={
                  <ProtectedRoutes
                    component={Pages.Species}
                    permissions={species.canViewSpecies}
                    fallbackPath={-1}
                  />
                }
              />
              <Route path={RoutesConstants.specieId}>
                <Route
                  path={RoutesConstants.profile}
                  element={
                    <ProtectedRoutes
                      component={Pages.SpecieProfile}
                      permissions={species.canViewSpecies}
                      fallbackPath={-1}
                    />
                  }
                />
              </Route>
              <Route
                path="*"
                element={
                  <Suspense fallback={<SuspenseFallback />}>
                    <Pages.PageNotFound />
                  </Suspense>
                }
              />
            </Route>

            <Route path="object/*">
              <Route
                path={RoutesConstants.list}
                element={
                  <ProtectedRoutes
                    component={Pages.Objects}
                    permissions={objects.canViewObjects}
                    fallbackPath={-1}
                  />
                }
              />
              <Route path={RoutesConstants.objectId}>
                <Route
                  path={RoutesConstants.profile}
                  element={
                    <ProtectedRoutes
                      component={Pages.ObjectProfile}
                      permissions={objects.canViewObjects}
                      fallbackPath={-1}
                    />
                  }
                />
              </Route>
              <Route
                path="*"
                element={
                  <Suspense fallback={<SuspenseFallback />}>
                    <Pages.PageNotFound />
                  </Suspense>
                }
              />
            </Route>
            {/* <Route path="regions" element={ <Suspense fallback={<SuspenseFallback />}><Pages.ConfigureRegions /></Suspense>} /> */}

            <Route
              path={RoutesConstants.messages}
              element={
                <ProtectedRoutes
                  component={Pages.Messages}
                  permissions={conversations.canViewConversations}
                  fallbackPath={-1}
                />
              }
            />
            <Route
              path={RoutesConstants.markerGroups}
              element={
                <ProtectedRoutes
                  component={Pages.MarkerGroups}
                  permissions={markerGroups.canView}
                  fallbackPath={-1}
                />
              }
            />
            <Route
              path={RoutesConstants.markerStyles}
              element={
                <ProtectedRoutes
                  component={Pages.MarkerStyles}
                  permissions={markerStyles.canView}
                  fallbackPath={-1}
                />
              }
            />
            <Route
              path={RoutesConstants.messages}
              element={
                <ProtectedRoutes
                  component={Pages.Messages}
                  permissions={conversations.canViewConversations}
                  fallbackPath={-1}
                />
              }
            />
            <Route
              path={RoutesConstants.dataPortal}
              element={
                <ProtectedRoutes
                  component={Pages.DataPortal}
                  permissions={
                    theme?.brandName ===
                      "Seacoast Indigenous Guardians Network" &&
                    auth.roles.includes("brand_admin")
                  }
                  fallbackPath={-1}
                />
              }
            />

            <Route path="category/*">
              <Route
                path={RoutesConstants.list}
                element={
                  <ProtectedRoutes
                    component={Pages.Categories}
                    permissions={categories.canViewCategories}
                    fallbackPath={-1}
                  />
                }
              />

              <Route
                path="*"
                element={
                  <Suspense fallback={<SuspenseFallback />}>
                    <Pages.PageNotFound />
                  </Suspense>
                }
              />
            </Route>

            <Route path="behaviors/*">
              <Route
                path={RoutesConstants.list}
                element={
                  <ProtectedRoutes
                    component={Pages.Behaviors}
                    permissions={behaviors.canViewBehaviors}
                    fallbackPath={-1}
                  />
                }
              />

              <Route
                path="*"
                element={
                  <Suspense fallback={<SuspenseFallback />}>
                    <Pages.PageNotFound />
                  </Suspense>
                }
              />
            </Route>

            <Route path="conditions/*">
              <Route
                path={RoutesConstants.list}
                element={
                  <ProtectedRoutes
                    component={Pages.Conditions}
                    permissions={conditions.canViewConditions}
                    fallbackPath={-1}
                  />
                }
              />

              <Route
                path="*"
                element={
                  <Suspense fallback={<SuspenseFallback />}>
                    <Pages.PageNotFound />
                  </Suspense>
                }
              />
            </Route>
            <Route path="brands/*">
              <Route
                path={RoutesConstants.list}
                element={
                  <ProtectedRoutes
                    component={Pages.Brands}
                    permissions={true}
                    fallbackPath={-1}
                  />
                }
              />

              <Route
                path={RoutesConstants.brandId}
                element={
                  <ProtectedRoutes
                    component={Pages.AddEditBrand}
                    permissions={true}
                    fallbackPath={-1}
                  />
                }
              />
              <Route
                path={RoutesConstants.editBrand}
                element={
                  <ProtectedRoutes
                    component={Pages.AddEditBrand}
                    permissions={true}
                    fallbackPath={-1}
                  />
                }
              />

              <Route
                path={RoutesConstants.addBrand}
                element={
                  <ProtectedRoutes
                    component={Pages.AddEditBrand}
                    permissions={true}
                    fallbackPath={-1}
                  />
                }
              />

              <Route
                path="*"
                element={
                  <Suspense fallback={<SuspenseFallback />}>
                    <Pages.PageNotFound />
                  </Suspense>
                }
              />
            </Route>

            <Route path="brand-details/*">
              <Route
                path=""
                element={
                  <ProtectedRoutes
                    component={Pages.BrandDetails}
                    permissions={brands.canViewBrandDetail}
                    fallbackPath={-1}
                  />
                }
              />
              <Route
                path={RoutesConstants.editBrandDetails}
                element={
                  <ProtectedRoutes
                    component={Pages.BrandDetails}
                    permissions={brands.canViewBrandDetail}
                    fallbackPath={-1}
                  />
                }
              />
            </Route>

            <Route path="types/*">
              <Route
                path={RoutesConstants.list}
                element={
                  <ProtectedRoutes
                    component={Pages.Types}
                    permissions={types.canViewType}
                    fallbackPath={-1}
                  />
                }
              />

              <Route
                path="*"
                element={
                  <Suspense fallback={<SuspenseFallback />}>
                    <Pages.PageNotFound />
                  </Suspense>
                }
              />
            </Route>
            <Route path="admin-tracking/*">
              <Route
                path={RoutesConstants.list}
                element={
                  <ProtectedRoutes
                    component={Pages.AdminsTracking}
                    permissions={trackings.canViewAdminTracking}
                    fallbackPath={-1}
                  />
                }
              />

              <Route
                path="*"
                element={
                  <Suspense fallback={<SuspenseFallback />}>
                    <Pages.PageNotFound />
                  </Suspense>
                }
              />
            </Route>

            <Route path="roles-permissions/*">
              <Route
                path={RoutesConstants.list}
                element={
                  <ProtectedRoutes
                    component={Pages.RolesPermissions}
                    permissions={roles.canViewRole}
                    fallbackPath="/"
                  />
                }
              />

              <Route
                path="*"
                element={
                  <Suspense fallback={<SuspenseFallback />}>
                    <Pages.PageNotFound />
                  </Suspense>
                }
              />
            </Route>

            <Route path="tagging/*">
              <Route
                path={RoutesConstants.list}
                element={
                  <ProtectedRoutes
                    component={Pages.Tags}
                    permissions={tags.canViewTag}
                    fallbackPath="/"
                  />
                }
              />

              <Route
                path="*"
                element={
                  <Suspense fallback={<SuspenseFallback />}>
                    <Pages.PageNotFound />
                  </Suspense>
                }
              />
            </Route>

            <Route path="reports/*">
              <Route
                path={RoutesConstants.list}
                element={
                  <ProtectedRoutes
                    component={Pages.Reports}
                    permissions={reports.canViewReports}
                    fallbackPath={-1}
                  />
                }
              />
              <Route
                path={RoutesConstants.addReport}
                element={
                  <ProtectedRoutes
                    component={Pages.AddEditReport}
                    permissions={reports.canCreateReports}
                    fallbackPath={-1}
                  />
                }
              />

              <Route path={RoutesConstants.reportsId}>
                <Route
                  path={RoutesConstants.editReport}
                  element={
                    <ProtectedRoutes
                      component={Pages.AddEditReport}
                      permissions={reports.canEditReports}
                      fallbackPath={-1}
                    />
                  }
                />
                <Route
                  path={RoutesConstants.analytics}
                  element={
                    <ProtectedRoutes
                      component={Pages.ReportsAnalytics}
                      permissions={reports.canViewAnalyticsReports}
                      fallbackPath={-1}
                    />
                  }
                />
                <Route
                  path={RoutesConstants.profile}
                  element={
                    <ProtectedRoutes
                      component={Pages.ReportsProfile}
                      permissions={reports.canViewReports}
                      fallbackPath={-1}
                    />
                  }
                />
                <Route
                  path={RoutesConstants.runReport}
                  element={
                    <ProtectedRoutes
                      component={Pages.RunReport}
                      permissions={reports.canViewProcessedVersionReports}
                      fallbackPath={-1}
                    />
                  }
                />
                <Route
                  path={RoutesConstants.reportVersion}
                  element={
                    <ProtectedRoutes
                      component={Pages.ResultObservations}
                      permissions={reports.canViewResultReports}
                      fallbackPath={-1}
                    />
                  }
                />
              </Route>

              <Route
                path="*"
                element={<ProtectedRoutes component={Pages.PageNotFound} />}
              />
            </Route>

            <Route path="notification-center/*">
              <Route
                path={RoutesConstants.list}
                element={
                  <ProtectedRoutes
                    component={Pages.NotificationCenter}
                    permissions={notifications.canViewNotifications}
                    fallbackPath={-1}
                  />
                }
              />

              <Route
                path="*"
                element={
                  <Suspense fallback={<SuspenseFallback />}>
                    <Pages.PageNotFound />
                  </Suspense>
                }
              />
            </Route>

            <Route
              path={RoutesConstants.systemSettings}
              element={
                <ProtectedRoutes
                  component={Pages.SyestemSettings}
                  permissions={universalSettings.canViewUniversalSettings}
                  fallbackPath={-1}
                />
              }
            />

            <Route
              path="geography"
              element={
                <ProtectedRoutes
                  component={Pages.AdminGeography}
                  permissions={
                    geographies.canViewGeographies &&
                    geographies.canViewGeographiesInSideBar
                  }
                  fallbackPath={-1}
                />
              }
            />

            <Route
              path="*"
              element={
                <Suspense fallback={<SuspenseFallback />}>
                  <Pages.PageNotFound />
                </Suspense>
              }
            />
          </Route>
        )}

        {/* <Route path="/mapconfigure" element={ <Suspense fallback={<SuspenseFallback />}><Pages.ConfigureRegions /></Suspense>} /> */}
        <Route
          path={`/polls-and-surveys/${RoutesConstants.submitSurveyId}`}
          element={
            <Suspense fallback={<SuspenseFallback />}>
              <Pages.SubmitSurvey />
            </Suspense>
          }
        />

        <Route
          path="*"
          element={<Navigate to={getAuthToken() ? `/` : "/auth/signin"} />}
        />
      </Routes>
    </div>
  );
}

export default App;

export function SuspenseFallback() {
  return (
    <div className="loader lo h-screen justify-center items-center flex w-full">
      <TailSpin
        height="50"
        width="50"
        color={useThemeColor()}
        ariaLabel="tail-spin-loading"
        radius="2"
        wrapperStyle={{}}
        wrapperClass="tailspin-loader"
        visible={true}
      />
    </div>
  );
}
